import React, { useEffect } from 'react';
import { ChatPanel } from './ChatPanel';
import { useAIChatStore } from '../../stores/aiChatStore';
import { useUserStats } from '../../hooks/useUserStats';
import { useTaskStore } from '../../stores/taskStore';
import { useHabitStore } from '../../stores/habitStore';
import { useProjectStore } from '../../stores/projectStore';
import { useUserStore } from '../../stores/userStore';
import { useAssessmentStore } from '../../stores/assessmentStore';

export function AIChat() {
  const { isOpen, setOpen, setContextData } = useAIChatStore();
  const { stats } = useUserStats();
  const tasksRecord = useTaskStore(state => state.tasks);
  const habits = useHabitStore(state => state.habits);
  const projects = useProjectStore(state => state.projects);
  const purposeStatement = useUserStore(state => state.purposeStatement);
  const vision = useUserStore(state => state.vision);
  const missions = useUserStore(state => state.missions);
  const getTalentContext = useAssessmentStore(state => state.getTalentContext);

  useEffect(() => {
    const allTasks = Object.values(tasksRecord || {}).flat();

    const activeTasks = allTasks.filter(t => !t.completed).length;
    const activeHabits = (habits || []).filter(h => !h.archivedAt).length;
    const activeProjects = (projects || []).filter(p => p.status === 'active').length;

    const recentActivity: string[] = [];
    const completedToday = allTasks.filter(t =>
      t.completed && t.pointRecord?.earnedAt &&
      new Date(t.pointRecord.earnedAt).toDateString() === new Date().toDateString()
    );
    if (completedToday.length > 0) {
      recentActivity.push(`${completedToday.length} tasks completed today`);
    }

    const missionEntries = stats.categories
      .map(c => ({ domain: c.name, mission: (missions || {})[c.id] }))
      .filter((m): m is { domain: string; mission: string } => !!m.mission?.trim());

    const { topTalents } = getTalentContext();

    setContextData({
      userLevel: stats.level,
      domains: stats.categories.map(c => ({
        id: c.id,
        name: c.name,
        level: c.level,
        rank: c.highestRank || undefined,
      })),
      foundation: {
        purpose: purposeStatement || undefined,
        vision: vision || undefined,
        missions: missionEntries,
        topTalents: topTalents.length > 0 ? topTalents : undefined,
      },
      activeTasks,
      activeHabits,
      activeProjects,
      recentActivity,
    });
  }, [stats, tasksRecord, habits, projects, purposeStatement, vision, missions, getTalentContext, setContextData]);

  // The launcher lives in the navigation (desktop rail item + mobile bottom-bar
  // centre button) — no floating widget; this renders just the panel.
  return (
    <>
      {isOpen && <ChatPanel onClose={() => setOpen(false)} />}
    </>
  );
}
